import { gql, useQuery, useMutation } from '@apollo/client';
import { POST_TITLE_LIMIT, POST_DESCRIPTION_LIMIT, characterCountColour } from '../../../constants';
import React, { useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd';
import {
    Segment,
    Header,
    Form,
    Input,
    TextArea,
    Button,
    Icon,
    Message,
    Divider,
    Dropdown,
    Placeholder
} from 'semantic-ui-react';

import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { GET_TAGS as GET_POSSIBLE_TAGS } from '../../../queries/tags';
import { MediaTile } from '../../../components/MediaTile';
import styles from './PostCompose.module.css';

/**
 * The audience's clock, so a schedule can be shown in their time as well as the scheduler's.
 * Null when the organisation has not set one, and then nothing extra is shown — which is
 * exactly what this screen did before.
 */
const GET_ORG_TIMEZONE = gql`
  query ComposeOrganisationTimezone {
    me { organisation { id name timezone } }
  }
`;

const GET_REVIEWERS = gql`
  query ContentReviewers {
    contentReviewers {
      id
      name
      photoUrl
      membershipId
    }
  }
`;

/**
 * "3:30pm Monday for readers in Asia/Kolkata" beneath the picker.
 *
 * Renders nothing when the organisation has no stated zone, or when it matches the viewer's —
 * a second copy of the same time is noise, and saying it twice makes people doubt the first.
 */
const AudienceTime = ({ localValue, timezone, organisationName }: {
    localValue: string;
    timezone: string | null;
    organisationName: string | null;
}) => {
    const viewerZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!timezone || timezone === viewerZone || !localValue) return null;

    const date = new Date(localValue);
    if (Number.isNaN(date.getTime())) return null;

    let rendered: string;
    try {
        rendered = new Intl.DateTimeFormat(undefined, {
            timeZone: timezone,
            weekday: 'short', day: 'numeric', month: 'short',
            hour: '2-digit', minute: '2-digit',
        }).format(date);
    } catch {
        // A stored zone the browser cannot resolve. The server validates on save, so this is
        // only reachable for a row written before that existed — say nothing rather than throw
        // and take the whole composer down.
        return null;
    }

    return (
        <div style={{ fontSize: '0.85em', color: 'rgba(0,0,0,.6)', marginTop: '0.4em' }}>
            {rendered} for {organisationName ?? 'readers'} ({timezone.replace(/_/g, ' ')})
        </div>
    );
};

const GET_IMAGE = gql`
  query Image($imageId: Int!) {
    image(id: $imageId) {
      id
      url
      contentType
      durationSeconds
    }
  }
`;

/**
 * Streams the author may publish into — CONTRIBUTE or above, in their own organisation.
 *
 * Deliberately not the `streams` list, which is everything they can *read*: a supporter can read
 * a community's stream and must not be offered it here, and a picker built from the wrong list
 * fails on submit rather than at render.
 */
const STREAMS_I_CAN_POST_TO = gql`
  query StreamsICanPostTo {
    streamsICanPostTo { id name isDefault }
  }
`;

const CREATE_POST = gql`
  mutation CreatePost($title: String!, $type: String!, $publishAt: String, $imageIds: [Int], $description: String!, $reviewerMembershipId: Int!, $streamId: Int) {
    createPost(title: $title, type: $type, publishAt: $publishAt, imageIds: $imageIds, description: $description, reviewerMembershipId: $reviewerMembershipId, streamId: $streamId) {
      # Post is a union; id must be selected per member type
      ... on CommunityUpdate { id }
      ... on CommunityPrayerRequest { id }
      ... on LeadershipUpdate { id }
      ... on LeadershipPrayerRequest { id }
      ... on Birthday { id }
    }
  }
`;

const UPDATE_POST_TAGS = gql`
  mutation UpdatePostTags($postId: Int!, $tagIds: [Int!]!) {
    updatePostTags(postId: $postId, tagIds: $tagIds) {
      id
      name
    }
  }
`;

interface ImageItemProps {
    id: string;
    onRemove: (id: string) => void;
}

const SelectedImageItem: React.FC<ImageItemProps> = ({ id, onRemove }) => {
    const { data, loading, error } = useQuery(GET_IMAGE, {
        variables: { imageId: parseInt(id) },
        skip: !id,
    });

    if (loading) {
        return (
            <div style={{ padding: '10px' }}>
                <Placeholder style={{ height: 80, width: 80, borderRadius: '8px' }}>
                    <Placeholder.Image />
                </Placeholder>
            </div>
        );
    }

    if (error || !data?.image) return null;

    return (
        <div style={{ padding: '10px', position: 'relative' }}>
            <MediaTile
                url={data.image.url}
                contentType={data.image.contentType}
                durationSeconds={data.image.durationSeconds}
                rounded
                style={{ width: '80px', height: '80px', objectFit: 'cover', border: '1px solid #ddd' }}
            />
            <Button
                icon="close"
                size="mini"
                color="red"
                circular
                onClick={(e) => {
                    e.stopPropagation();
                    onRemove(id);
                }}
                style={{
                    position: 'absolute',
                    top: '2px',
                    right: '2px',
                    zIndex: 10,
                    margin: 0,
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                }}
            />
        </div>
    );
};

const PostCompose: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const [orderedImageIds, setOrderedImageIds] = useState<string[]>(() => {
        const params = new URLSearchParams(location.search);
        const ids = params.get('images');
        return ids ? ids.split(',').filter(Boolean) : [];
    });

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const idString = orderedImageIds.join(',');
        if (idString) {
            params.set('images', idString);
        } else {
            params.delete('images');
        }
        navigate({ search: params.toString() }, { replace: true });
    }, [orderedImageIds, navigate]);

    // Data Queries
    const { loading: reviewersLoading, data: reviewersData } = useQuery(GET_REVIEWERS);
    const { loading: tagsLoading, data: tagsData } = useQuery(GET_POSSIBLE_TAGS, {
        variables: {
            showPeopleTags: false,
            showCourseTags: true,
        }
    });

    // Mutations
    const [createPost, { loading: mutationLoading }] = useMutation(CREATE_POST);
    const { data: streamData } = useQuery(STREAMS_I_CAN_POST_TO);
    const { data: orgData } = useQuery(GET_ORG_TIMEZONE);
    const audienceTimezone: string | null = orgData?.me?.organisation?.timezone ?? null;
    const audienceName: string | null = orgData?.me?.organisation?.name ?? null;
    const streams = streamData?.streamsICanPostTo ?? [];
    const [streamId, setStreamId] = useState<number | null>(null);
    const [updatePostTags] = useMutation(UPDATE_POST_TAGS);

    const getLocalISOString = (date: Date) => {
        const tzOffset = date.getTimezoneOffset() * 60000;
        return new Date(date.getTime() - tzOffset).toISOString().slice(0, 16);
    };

    const [postData, setPostData] = useState({
        title: '',
        description: '',
        type: 'POST',
        // A Membership id, not a User id — see the note on User.membershipId.
        reviewerMembershipId: '',
        publishAt: getLocalISOString(new Date()),
        tagIds: [] as number[], // Local state for tags
    });

    const [statusMessage, setStatusMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(orderedImageIds);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        setOrderedImageIds(items);
    };

    const handleRemoveImage = (idToRemove: string) => {
        setOrderedImageIds(prev => prev.filter(id => id !== idToRemove));
    };

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setPostData(prev => ({ ...prev, [name]: value }));
    }, []);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setStatusMessage(null);

        try {
            // 1. Create the Post
            const { data: postResponse } = await createPost({
                variables: {
                    title: postData.title,
                    type: postData.type,
                    description: postData.description,
                    publishAt: new Date(postData.publishAt).toISOString(),
                    imageIds: orderedImageIds.map(id => parseInt(id)),
                    reviewerMembershipId: parseInt(postData.reviewerMembershipId),
                    // null means the organisation's default stream — the server decides, so a
                    // client that has never heard of streams behaves identically.
                    streamId,
                }
            });

            const newPostId = postResponse?.createPost?.id;

            // 2. If tags exist, update them for the new post
            if (newPostId && postData.tagIds.length > 0) {
                await updatePostTags({
                    variables: {
                        postId: parseInt(newPostId),
                        tagIds: postData.tagIds
                    }
                });
            }

            if (newPostId) {
                setStatusMessage({ type: 'success', text: 'Post and tags updated successfully!' });
                setTimeout(() => navigate(`/gallery`), 2000);
            }
        } catch (err: any) {
            console.error('Error creating post:', err);
            setStatusMessage({ type: 'error', text: err.message || 'Failed to create post.' });
        }
    };

    const tagOptions = tagsData?.tags?.map((tag: any) => ({
        key: tag.id,
        text: tag.name,
        value: parseInt(tag.id)
    })) || [];

    return (
        <ResponsiveContainer>
        <Segment raised padded className={styles.composeContainer}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Header as='h2' style={{ margin: 0 }}>
                    <Icon name='pencil alternate' color='blue' />
                    <Header.Content>
                        Create Content
                        <Header.Subheader>URL updates automatically on changes.</Header.Subheader>
                    </Header.Content>
                </Header>
                <Button
                    color='blue'
                    icon
                    labelPosition='left'
                    onClick={() => navigate(`/gallery?images=${orderedImageIds.join(',')}`)}
                >
                    <Icon name='images' />
                    Select Images
                </Button>
            </div>

            <Divider />

            {orderedImageIds.length > 0 ? (
                <div style={{
                    marginBottom: '2em',
                    background: '#fcfcfc',
                    padding: '10px',
                    borderRadius: '8px',
                    border: '1px dashed #ccc'
                }}>
                    <DragDropContext onDragEnd={onDragEnd}>
                        <Droppable droppableId="images" direction="horizontal">
                            {(provided) => (
                                <div
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    style={{ display: 'flex', overflowX: 'auto', minHeight: '100px', padding: '5px' }}
                                >
                                    {orderedImageIds.map((id, index) => (
                                        <Draggable key={id} draggableId={id} index={index}>
                                            {(provided) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    {...provided.dragHandleProps}
                                                >
                                                    <SelectedImageItem id={id} onRemove={handleRemoveImage} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            ) : (
                <Message info size="tiny">
                    <Icon name="info circle" /> No images attached.
                </Message>
            )}

            {statusMessage && (
                <Message
                    positive={statusMessage.type === 'success'}
                    negative={statusMessage.type === 'error'}
                    content={statusMessage.text}
                />
            )}

            <Form onSubmit={handleSubmit} loading={mutationLoading}>
                <Form.Group widths='equal'>
                    {/* Only shown when there is a choice to make. One stream means the
                        organisation has not split its publishing up, and asking would be a
                        question with one answer. */}
                    {streams.length > 1 && (
                        <Form.Field>
                            <label>Stream</label>
                            <Dropdown
                                selection
                                placeholder="Where should this go?"
                                value={streamId ?? streams.find((s: any) => s.isDefault)?.id}
                                options={streams.map((s: any) => ({
                                    key: s.id,
                                    text: s.isDefault ? `${s.name} (default)` : s.name,
                                    value: s.id,
                                }))}
                                onChange={(_, d) => setStreamId(d.value as number)}
                            />
                        </Form.Field>
                    )}
                    <Form.Field required>
                        <label>Post Type</label>
                        <Dropdown
                            selection
                            options={[
                                { key: 'post', text: 'Post', value: 'COMMUNITY_UPDATE', icon: 'newspaper' },
                                { key: 'event', text: 'Prayer Request', value: 'COMMUNITY_PRAYER_REQUEST', icon: 'calendar' },
                                { key: 'birthday', text: 'Birthday', value: 'BIRTHDAY', icon: 'birthday cake' },
                            ]}
                            value={postData.type}
                            onChange={(_, { value }) => setPostData(p => ({ ...p, type: value as string }))}
                        />
                    </Form.Field>
                    <Form.Field required>
                        <label>Publish Date</label>
                        <Input type='datetime-local' name='publishAt' value={postData.publishAt} onChange={handleInputChange} />
                        {/* The whole reason the organisation carries a timezone. The input is in
                            the browser's zone, so without this "Sunday 9am" silently means 9am
                            wherever the scheduler happens to be — which stopped being the same
                            place as the audience once posts could be authored into another
                            organisation's stream. */}
                        <AudienceTime
                            localValue={postData.publishAt}
                            timezone={audienceTimezone}
                            organisationName={audienceName}
                        />
                    </Form.Field>
                </Form.Group>

                <Form.Field required>
                    <label>Assigned Reviewer</label>
                    <Dropdown
                        placeholder='Select Reviewer'
                        fluid search selection
                        loading={reviewersLoading}
                        options={reviewersData?.contentReviewers.map((rev: any) => ({
                            key: rev.membershipId, text: rev.name, value: rev.membershipId, image: { avatar: true, src: rev.photoUrl }
                        })) || []}
                        value={postData.reviewerMembershipId}
                        onChange={(_, { value }) => setPostData(p => ({ ...p, reviewerMembershipId: value as string }))}
                    />
                </Form.Field>

                <Form.Field>
                    <label>Tags</label>
                    <Dropdown
                        placeholder='Add tags...'
                        fluid
                        multiple
                        search
                        selection
                        loading={tagsLoading}
                        options={tagOptions}
                        value={postData.tagIds}
                        onChange={(_, { value }) => setPostData(p => ({ ...p, tagIds: value as number[] }))}
                    />
                </Form.Field>

                <Form.Field required>
                    <label>Title</label>
                    <Input name='title' value={postData.title} onChange={handleInputChange} />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(postData.title.length, POST_TITLE_LIMIT) }}>
                        {postData.title.length} / {POST_TITLE_LIMIT}
                    </div>
                </Form.Field>

                <Form.Field required>
                    <label>Description</label>
                    <TextArea name='description' rows={6} value={postData.description} onChange={handleInputChange} />
                    <div style={{ textAlign: 'right', fontSize: '0.8em', color: characterCountColour(postData.description.length, POST_DESCRIPTION_LIMIT) }}>
                        {postData.description.length} / {POST_DESCRIPTION_LIMIT}
                    </div>
                </Form.Field>

                <Button
                    primary
                    fluid
                    size='large'
                    type='submit'
                    disabled={
                        !postData.title || !postData.description || !postData.reviewerMembershipId
                        || postData.title.length > POST_TITLE_LIMIT
                        || postData.description.length > POST_DESCRIPTION_LIMIT
                    }
                >
                    <Icon name='send' />
                    Submit for Review
                </Button>
            </Form>
        </Segment>
        </ResponsiveContainer>
    );
};

export { PostCompose };
